import { useEffect, useMemo, useState } from "react";

import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  SPORTSBOOK_KPI_ALL_FILTER_VALUE,
  type SportsbookKpiFilterOptions,
  type SportsbookKpiFilterValues,
} from "../../../../queries/sportsbook-kpis/sportsbookKpiFilters";

import styles from "./SportsbookKpiFiltersSection.module.css";

export const EMPTY_SPORTSBOOK_KPI_FILTERS: Required<SportsbookKpiFilterValues> =
  {
    sport: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    league: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    bet_type: [],
    money_type: [],
  };

type Props = {
  value: Required<SportsbookKpiFilterValues>;
  options: SportsbookKpiFilterOptions;
  betTypeAvailable?: boolean;
  loading?: boolean;
  onApply: (filters: Required<SportsbookKpiFilterValues>) => void;
};

const SportsbookKpiFiltersSection = ({
  value,
  options,
  betTypeAvailable = false,
  loading = false,
  onApply,
}: Props) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const hasChanges = useMemo(
    () =>
      (Object.keys(draft) as (keyof SportsbookKpiFilterValues)[]).some(
        (key) =>
          JSON.stringify(draft[key]) !== JSON.stringify(value[key]),
      ),
    [draft, value],
  );

  const updateDraft = (
    key: keyof SportsbookKpiFilterValues,
    nextValue: string | string[],
  ) => {
    setDraft((current) => ({
      ...current,
      [key]: nextValue,
      ...(key === "sport"
        ? { league: SPORTSBOOK_KPI_ALL_FILTER_VALUE }
        : {}),
    }));
  };

  const fields = [
    {
      key: "sport" as const,
      label: AppStrings.SPORT,
      options: options.sports,
      disabled: false,
      multiple: false,
      visible: true,
    },
    {
      key: "league" as const,
      label: AppStrings.LEAGUE,
      options: options.leagues,
      disabled: draft.sport === SPORTSBOOK_KPI_ALL_FILTER_VALUE,
      multiple: false,
      visible: true,
    },
    {
      key: "bet_type" as const,
      label: AppStrings.BET_TYPE,
      options: options.betTypes,
      disabled: !betTypeAvailable,
      multiple: true,
      visible: true,
    },
    {
      key: "money_type" as const,
      label: AppStrings.SPORTSBOOK_PROFITABILITY_CURRENCY_TYPE,
      options: options.currencyTypes,
      disabled: false,
      multiple: true,
      visible: true,
    },
  ].filter((field) => field.visible !== false);

  return (
    <section
      className={styles.section}
      aria-label={AppStrings.PAM_REPORT_FILTERS}
      data-testid="sportsbook-kpi-filters-section"
    >
      <div className={styles.fields}>
        {fields.map((field) => (
          <div className={styles.field} key={field.key}>
            <span className={styles.label}>{field.label}</span>
            <Select
              className={styles.filterSelect}
              mode={field.multiple ? "multiple" : undefined}
              value={draft[field.key]}
              options={field.options}
              onChange={(next) =>
                updateDraft(field.key, next as string | string[])
              }
              aria-label={field.label}
              placeholder={
                field.key === "bet_type"
                  ? AppStrings.SPORTSBOOK_KPI_ALL_BET_TYPES
                  : field.key === "money_type"
                    ? AppStrings.SPORTSBOOK_KPI_ALL_CURRENCY_TYPES
                    : undefined
              }
              maxTagCount={field.multiple ? "responsive" : undefined}
              showSearch
              optionFilterProp="label"
              disabled={field.disabled}
              loading={loading}
            />
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        <Button
          intent="neutral"
          variant="outline"
          size="large"
          onClick={() => setDraft(EMPTY_SPORTSBOOK_KPI_FILTERS)}
        >
          {AppStrings.CLEAR_FILTERS}
        </Button>
        <Button
          intent="primary"
          size="large"
          onClick={() => onApply(draft)}
          disabled={!hasChanges}
        >
          {AppStrings.APPLY}
        </Button>
      </div>
    </section>
  );
};

export default SportsbookKpiFiltersSection;
